import { FC, useMemo, useCallback } from "react";
import { useTaxonomyContext } from "../../../../contexts/Taxonomy/TaxonomyContext";
import TreeSelect from "../../TreeSelect/TreeSelect";
import { transformToTree } from "../../TreeSelect/utils";

interface FaiByDepartmentsAndRolesInputProps {
  onFaiSelect: (
    selectedDepartmentIds: string[],
    selectedRoleIds: string[]
  ) => void;
  selectedItems: string[];
  id?: string;
  onClose?: () => void;
  showDoneButton?: boolean;
}

interface TransformedFaiItem {
  id: string;
  parentId: string;
  name: string;
  isDepartment?: boolean;
}

export const FaiByDepartmentsAndRolesInput: FC<
  FaiByDepartmentsAndRolesInputProps
> = ({
  onFaiSelect,
  selectedItems = [],
  id = "fai-input",
  onClose,
  showDoneButton,
}) => {
  const {
    faiDepartments,
    faiDepartmentMap,
    faiRoleMap,
    faiDepartmentRolesMap,
  } = useTaxonomyContext();

  // displaySelectedItems: if a department is selected, add all its roles to show in the TreeSelect

  const displaySelectedItems = useMemo(() => {
    const resultSet = new Set(selectedItems);

    selectedItems.forEach((id) => {
      if (faiDepartmentMap.has(id)) {
        // This is a department, add all its roles
        const roleIds = faiDepartmentRolesMap.get(id) || [];
        roleIds.forEach((roleId) => {
          resultSet.add(roleId);
        });
      }
    });

    return Array.from(resultSet);
  }, [selectedItems, faiDepartmentMap, faiDepartmentRolesMap]);

  // Transform FAI departments data for TreeSelect
  const transformedData = useMemo(() => {
    const result: TransformedFaiItem[] = [];

    faiDepartments.forEach((dept) => {
      // Add department
      result.push({
        id: dept.id,
        parentId: "0",
        name: dept.name,
        isDepartment: true,
      });

      // Add roles
      dept.roles.forEach((role) => {
        result.push({
          id: role.id,
          parentId: dept.id,
          name: role.name,
          isDepartment: false,
        });
      });
    });

    return result;
  }, [faiDepartments]);

  const allOptions = useMemo(
    () => transformToTree(transformedData, (item) => item.name),
    [transformedData]
  );

  const handleSelectionChange = useCallback(
    (selectedIds: string[]) => {
      const selectedDepartmentIds: string[] = [];
      const selectedRoleIds: string[] = [];

      // Separate departments and roles
      selectedIds.forEach((id) => {
        if (faiDepartmentMap.has(id)) {
          selectedDepartmentIds.push(id);
        } else if (faiRoleMap.has(id)) {
          selectedRoleIds.push(id);
        }
      });

      // Optimize: if all roles of a department are selected, keep only the department
      const rolesToRemove = new Set<string>();
      const departmentsToAdd = new Set(selectedDepartmentIds);

      faiDepartmentRolesMap.forEach((roleIds, deptId) => {
        const selectedRolesInDept = roleIds.filter((roleId) =>
          selectedRoleIds.includes(roleId)
        );

        // If all roles in this department are selected, add department and mark roles for removal
        if (
          selectedRolesInDept.length === roleIds.length &&
          roleIds.length > 0
        ) {
          departmentsToAdd.add(deptId);
          roleIds.forEach((roleId) => rolesToRemove.add(roleId));
        }
      });

      const finalDepartmentIds = Array.from(departmentsToAdd);
      const finalRoleIds = selectedRoleIds.filter(
        (roleId) => !rolesToRemove.has(roleId)
      );

      onFaiSelect(finalDepartmentIds, finalRoleIds);
    },
    [onFaiSelect, faiDepartmentMap, faiRoleMap, faiDepartmentRolesMap]
  );

  return (
    <TreeSelect
      id={id}
      items={allOptions}
      selectedItems={displaySelectedItems}
      onSelectItems={handleSelectionChange}
      onClose={onClose}
      allExpanded
      showDoneButton={showDoneButton}
      checkboxTestId="scoring-fai-checkbox-btn"
    />
  );
};
